import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import { aiSSEService } from '../../services/aiSSEService';
import { lessonService } from '../../services/lessonService';
import { slidesService } from '../../services/slidesService';
import { imageService } from '../../services/imageService';
import { injectImagesIntoHTML } from '../../utils/slidePreview';
import { useSSEValidation } from '../../hooks/useSSEValidation';
import { useLessonStore } from '../../store/lessonStore';
import { useEditorStore } from '../../store/editorStore';
import { ChatInterface } from '../../components/common/ChatInterface';
import type { ChatMessage, Image, SlideData, SlidesHTML } from '../../types';

interface Phase2Props {
  lessonId: string;
  readOnly?: boolean;
}

interface FlatSlide extends SlideData {
  stageName: string;
}

type SaveStatus = 'idle' | 'saving' | 'saved';

/**
 * Replaces the inner HTML of the Nth <section> in a full MARP HTML document.
 * Preserves the section's attributes (Bespoke classes, data-marpit-fragment, etc.).
 */
function patchSectionContent(fullHTML: string, sectionIndex: number, newInnerHTML: string): string {
  let count = 0;
  return fullHTML.replace(/<section([^>]*)>([\s\S]*?)<\/section>/g, (match, attrs) => {
    if (count++ === sectionIndex) return `<section${attrs}>${newInnerHTML}</section>`;
    return match;
  });
}

/** Extracts the inner HTML of the Nth <section> from the full MARP HTML. */
function extractSectionInnerHTML(fullHTML: string, sectionIndex: number): string {
  let count = 0;
  let found = '';
  fullHTML.replace(/<section[^>]*>([\s\S]*?)<\/section>/g, (_, inner) => {
    if (count++ === sectionIndex) found = inner;
    return '';
  });
  return found;
}

/**
 * Builds a map from MARP section index → content slide index (or -1 for
 * structural slides: the intro/overview slides and per-stage dividers).
 *
 * The slides-service always generates:
 *   [introCount intro slides] + for each stage: [1 divider + N content slides]
 *
 * introCount = totalSections - stages.length - totalContentSlides
 */
function buildSectionToContentMap(
  fullMarpHTML: string,
  stages: Array<{ name: string }>,
  slideMapping: Record<string, number>,
): number[] {
  const totalSections = (fullMarpHTML.match(/<section[^>]*>/g) ?? []).length;
  const totalContent = stages.reduce((s, st) => s + (slideMapping[st.name] ?? 2), 0);
  const introCount = Math.max(0, totalSections - stages.length - totalContent);

  const map: number[] = new Array(totalSections).fill(-1);
  let contentIdx = 0;
  let secIdx = introCount;

  for (const stage of stages) {
    secIdx++; // section divider slide — stays -1
    const count = slideMapping[stage.name] ?? 2;
    for (let i = 0; i < count; i++) {
      if (secIdx < totalSections) map[secIdx++] = contentIdx++;
    }
  }

  return map;
}

/**
 * Wraps the full MARP HTML for iframe use:
 *  1. Patches history.replaceState/pushState — Bespoke.js calls these and they
 *     throw SecurityError inside blob-URL iframes.
 *  2. Injects a MutationObserver that watches for Bespoke's active-slide class
 *     change and posts { type:'slide-change', index:N } to the parent window.
 *  3. Makes every section contentEditable when not readOnly.
 *  4. Optionally navigates to startIndex after Bespoke.js initialises, so the
 *     deck reopens on the same slide after a chat-driven patch + reload.
 */
function buildDeckDoc(
  fullMarpHTML: string,
  startMarpIndex = 0,
  editable = false,
  contentMap: number[] = [],
): string {
  const historyPatch = `<script id="_p2_hist">(function(){
  try{history.replaceState=function(){};history.pushState=function(){};}catch(e){}
}());</script>`;

  const mapJSON = JSON.stringify(contentMap);

  // MARP renders slides as <svg data-marpit-svg> elements inside div#:$p.
  // Bespoke.js adds/removes 'bespoke-marp-active' on those SVG elements —
  // NOT on <section> elements. Watch the SVGs, not the sections.
  const initScript = `<script id="_p2_init">(function(){
  var CMAP=${mapJSON};
  window.addEventListener('load',function(){
    var deck=document.getElementById(':$p');
    var svgSlides=Array.from(
      deck?deck.querySelectorAll('svg[data-marpit-svg]')
          :document.querySelectorAll('svg[data-marpit-svg]')
    );
    ${editable
      ? "document.querySelectorAll('section').forEach(function(s){s.contentEditable='true';s.style.outline='none';});"
      : ''}
    function report(){
      var marpIdx=svgSlides.findIndex(function(s){
        return s.classList.contains('bespoke-marp-active');
      });
      if(marpIdx>=0){
        var contentIdx=marpIdx<CMAP.length?CMAP[marpIdx]:-1;
        try{window.parent.postMessage({type:'slide-change',marpIndex:marpIdx,contentIndex:contentIdx},'*');}catch(e){}
      }
    }
    new MutationObserver(report).observe(deck||document.body,{subtree:true,attributes:true,attributeFilter:['class']});
    ${startMarpIndex > 0
      ? `setTimeout(function(){
      for(var i=0;i<${startMarpIndex};i++){
        document.dispatchEvent(new KeyboardEvent('keydown',{key:'ArrowRight',bubbles:true,cancelable:true}));
      }
    },200);`
      : ''}
    setTimeout(report,100);
  });
}());</script>`;

  let html = fullMarpHTML;
  html = html.replace(/(<head[^>]*>)/i, '$1' + historyPatch);
  html = html.includes('</head>')
    ? html.replace('</head>', initScript + '</head>')
    : initScript + html;
  return html;
}

/** Capture the iframe's current HTML, stripping our injected scripts so they
 *  don't accumulate across save/reload cycles. */
function captureCleanHTML(doc: Document): string {
  const cloned = doc.documentElement.cloneNode(true) as HTMLElement;
  ['_p2_hist', '_p2_init'].forEach((id) => cloned.querySelector(`#${id}`)?.remove());
  return '<!DOCTYPE html>' + cloned.outerHTML;
}

/**
 * Parses the MARP HTML and returns all img-placeholder entries with their
 * data-id, inner text (generation prompt), and 1-based MARP section index (slide number).
 */
function extractPlaceholders(
  html: string,
): Array<{ placeholder_id: string; prompt: string; slide_number: number }> {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sections = Array.from(doc.querySelectorAll('section'));
  return Array.from(doc.querySelectorAll('div.img-placeholder[data-id]'))
    .map((div) => {
      const section = div.closest('section');
      const slideNumber = section ? sections.indexOf(section) + 1 : 0;
      return {
        placeholder_id: div.getAttribute('data-id') ?? '',
        prompt: div.textContent?.trim() ?? '',
        slide_number: slideNumber,
      };
    })
    .filter((p) => p.placeholder_id);
}

export const Phase2Slides: React.FC<Phase2Props> = ({ lessonId, readOnly = false }) => {
  const { currentVersion, updateSlidesHTML, patchSlideNotes } = useLessonStore();
  const { setPhase, markDirty } = useEditorStore();
  const { refineStage: refineStageSSE } = useSSEValidation();
  const queryClient = useQueryClient();

  const lessonPlan = currentVersion?.lesson_plan ?? { stages: [] };
  const slidesHTML = currentVersion?.slides_html ?? {};
  const stages = lessonPlan.stages;
  const versionNumber = currentVersion?.version_number ?? 1;

  const fullMarpHTML =
    (slidesHTML as Record<string, unknown>)['__marp_full_html__'] as string ?? '';

  const allSlides: FlatSlide[] = stages.flatMap((stage) =>
    (slidesHTML[stage.name] ?? []).map((slide) => ({ ...slide, stageName: stage.name }))
  );

  // currentSlideIndex = index into allSlides (content slides only, used for chat)
  // currentMarpIndex  = raw Bespoke.js section index (used for patching the MARP HTML)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentMarpIndex, setCurrentMarpIndex] = useState(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [notesOpen, setNotesOpen] = useState(true);
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
  const [proceedError, setProceedError] = useState('');
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [blobUrl, setBlobUrl] = useState('');

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSlideIndexRef = useRef(currentSlideIndex);
  const currentMarpIndexRef = useRef(currentMarpIndex);
  const slidesHTMLRef = useRef(slidesHTML);
  const fullMarpHTMLRef = useRef(fullMarpHTML);
  const blobUrlRef = useRef('');
  const initializedRef = useRef(false);
  const imagesRef = useRef<Image[]>([]);

  useEffect(() => { currentSlideIndexRef.current = currentSlideIndex; }, [currentSlideIndex]);
  useEffect(() => { currentMarpIndexRef.current = currentMarpIndex; }, [currentMarpIndex]);
  useEffect(() => { slidesHTMLRef.current = slidesHTML; }, [slidesHTML]);
  useEffect(() => { fullMarpHTMLRef.current = fullMarpHTML; }, [fullMarpHTML]);

  // ── Blob URL management ──────────────────────────────────────────────────

  const createBlobUrl = useCallback((html: string, startMarpIndex = 0) => {
    const slideMapping = (currentVersion?.slide_mapping ?? {}) as Record<string, number>;
    const contentMap = buildSectionToContentMap(html, stages, slideMapping);
    const displayHtml = injectImagesIntoHTML(html, imagesRef.current);
    const doc = buildDeckDoc(displayHtml, startMarpIndex, !readOnly, contentMap);
    const blob = new Blob([doc], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = url;
    setBlobUrl(url);
  }, [readOnly, currentVersion, stages]);

  // Reset initialization when slides are cleared (e.g. after plan regeneration in Phase 1)
  // so the next generate re-inits properly.
  useEffect(() => {
    if (!fullMarpHTML) {
      initializedRef.current = false;
    }
  }, [fullMarpHTML]);

  // Build the initial blob URL once when fullMarpHTML first becomes available.
  useEffect(() => {
    if (fullMarpHTML && !initializedRef.current) {
      initializedRef.current = true;
      createBlobUrl(fullMarpHTML, 0);
    }
  }, [fullMarpHTML, createBlobUrl]);

  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  // ── Image injection: fetch generated images and show them in the blob preview ──

  const { data: imageData } = useQuery({
    queryKey: ['images', lessonId, versionNumber],
    queryFn: () => imageService.getImages(lessonId, versionNumber),
    refetchInterval: (query) => {
      const imgs = query.state.data?.images ?? [];
      return imgs.some((img) => img.status === 'generating') ? 5000 : false;
    },
  });

  useEffect(() => {
    const imgs = imageData?.images ?? [];
    imagesRef.current = imgs;
    if (!fullMarpHTMLRef.current || imgs.every((img) => !img.blob_url)) return;
    createBlobUrl(fullMarpHTMLRef.current, currentMarpIndexRef.current);
  }, [imageData, createBlobUrl]);

  // ── postMessage: track active slide from Bespoke.js ─────────────────────

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type !== 'slide-change') return;
      const marpIdx = e.data.marpIndex as number;
      const contentIdx = e.data.contentIndex as number;
      if (typeof marpIdx === 'number') setCurrentMarpIndex(marpIdx);
      if (typeof contentIdx === 'number' && contentIdx >= 0) setCurrentSlideIndex(contentIdx);
      // TODO: use contentIdx for refinement UI once mapping is stable
      console.warn('[Phase2] slide-change', { marpIndex: marpIdx, contentIndex: contentIdx });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const currentSlide = allSlides[currentSlideIndex] ?? null;

  // ── Persist full iframe HTML ─────────────────────────────────────────────

  const saveFullHTML = useCallback(async (silent = false) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || readOnly) return;
    const cleanHTML = captureCleanHTML(doc);
    fullMarpHTMLRef.current = cleanHTML;
    const merged = {
      ...(slidesHTMLRef.current as Record<string, unknown>),
      __marp_full_html__: cleanHTML,
    };
    if (!silent) setSaveStatus('saving');
    await lessonService.saveVersion(lessonId, currentVersion?.version_number ?? 1, {
      slides_html: merged,
    });
    updateSlidesHTML(merged as unknown as SlidesHTML);
    markDirty();
    if (!silent) {
      setSaveStatus('saved');
      if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
      savedStatusTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
    }
  }, [readOnly, lessonId, currentVersion, updateSlidesHTML, markDirty]);

  // ── WYSIWYG: debounce-save on any section edit ───────────────────────────

  const handleIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    if (!readOnly) {
      // Belt-and-suspenders: the injected script already sets contentEditable,
      // but do it here too in case the script ran before sections were ready.
      doc.querySelectorAll('section').forEach((s) => {
        (s as HTMLElement).contentEditable = 'true';
        (s as HTMLElement).style.outline = 'none';
      });
      doc.addEventListener('input', () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => void saveFullHTML(), 1500);
      });
    }
  }, [readOnly, saveFullHTML]);

  const flushEdits = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    await saveFullHTML(true);
  }, [saveFullHTML]);

  // ── Per-slide AI chat ────────────────────────────────────────────────────

  const handleChatSend = useCallback(async (message: string) => {
    if (!currentSlide || !message.trim()) return;
    setIsChatLoading(true);

    const stageSlides = (slidesHTMLRef.current[currentSlide.stageName] ?? []) as SlideData[];
    const stageLocalIndex = stageSlides.findIndex(
      (s) => s.slide_number === currentSlide.slide_number
    );

    // Extract the actual rendered HTML for this section from the MARP deck so
    // the LLM sees real content, not the empty stub stored after export().
    const liveHTML = extractSectionInnerHTML(fullMarpHTMLRef.current, currentMarpIndexRef.current);
    const enrichedSlides = stageSlides.map((s, i) =>
      i === stageLocalIndex ? { ...s, html: liveHTML } : s
    );

    await refineStageSSE({
      lesson_id: lessonId,
      version: currentVersion?.version_number ?? 1,
      stage_name: currentSlide.stageName,
      message: `Slide ${stageLocalIndex + 1} ONLY: ${message}`,
      current_slides: enrichedSlides,
    });

    const freshData = await lessonService.getById(lessonId);
    if (freshData?.version?.slides_html) {
      const freshHTML = freshData.version.slides_html;

      // Get the refined inner HTML for this slide and patch it into the full deck.
      const refinedSlide = (freshHTML[currentSlide.stageName] ?? []).find(
        (s) => s.slide_number === currentSlide.slide_number
      );
      if (refinedSlide?.html) {
        // Use the MARP section index (not the content index) to target the right <section>.
        const targetMarpIdx = currentMarpIndexRef.current;
        const patched = patchSectionContent(
          fullMarpHTMLRef.current, targetMarpIdx, refinedSlide.html
        );
        fullMarpHTMLRef.current = patched;

        const merged = {
          ...(slidesHTMLRef.current as Record<string, unknown>),
          __marp_full_html__: patched,
        };
        await lessonService.saveVersion(lessonId, currentVersion?.version_number ?? 1, {
          slides_html: merged,
        });
        updateSlidesHTML(merged as unknown as SlidesHTML);
        markDirty();

        // Reload the deck blob URL, restoring position to the refined slide.
        createBlobUrl(patched, targetMarpIdx);
      }

      // Merge chat history back in.
      const prev = slidesHTMLRef.current;
      const newUser: ChatMessage = { role: 'user', content: message };
      const newAI: ChatMessage = { role: 'assistant', content: 'Slide updated.' };
      const withHistory: SlidesHTML = {};
      for (const [stage, slides] of Object.entries(freshHTML)) {
        if (stage === '__marp_full_html__') continue;
        withHistory[stage] = (slides as SlideData[]).map((s) => {
          const existing = (prev[stage] ?? []).find((e) => e.slide_number === s.slide_number);
          const history = existing?.chat_history ?? [];
          const isTarget =
            stage === currentSlide.stageName && s.slide_number === currentSlide.slide_number;
          return { ...s, chat_history: isTarget ? [...history, newUser, newAI] : history };
        });
      }
      updateSlidesHTML(withHistory);
    }

    setIsChatLoading(false);
  }, [currentSlide, lessonId, currentVersion, refineStageSSE, updateSlidesHTML, markDirty, createBlobUrl]);

  // ── Regenerate all ───────────────────────────────────────────────────────

  const handleRegenerateAll = useCallback(async () => {
    setIsRegenerating(true);
    setRegenerateConfirmOpen(false);
    const versionNumber = currentVersion?.version_number ?? 1;
    try {
      const result = await slidesService.export(lessonId, versionNumber, 'html');
      const marpHTML = result.html ?? '';

      const slideMapping = currentVersion?.slide_mapping ?? {};
      const perStage: Record<string, Array<{ slide_number: number; html: string; image_placeholders: never[]; chat_history: never[] }>> = {};
      for (const stage of stages) {
        const count = (slideMapping as Record<string, number>)[stage.name] ?? 2;
        perStage[stage.name] = Array.from({ length: count }, (_, i) => ({
          slide_number: i + 1,
          html: '',
          image_placeholders: [],
          chat_history: [],
        }));
      }

      const slidesPayload = { ...perStage, __marp_full_html__: marpHTML };
      await lessonService.saveVersion(lessonId, versionNumber, {
        slides_html: slidesPayload,
        phase: 2,
      });
      updateSlidesHTML(slidesPayload as unknown as SlidesHTML);
      markDirty();
      setCurrentSlideIndex(0);

      // Force immediate blob URL rebuild — don't rely on the useEffect detecting
      // a change in fullMarpHTML, which won't fire if the HTML string is identical.
      imagesRef.current = [];
      initializedRef.current = true;
      createBlobUrl(marpHTML, 0);

      // Clear stale images from the DB and React Query cache.
      await imageService.clearImages(lessonId, versionNumber);
      queryClient.removeQueries({ queryKey: ['images', lessonId, versionNumber] });
    } finally {
      setIsRegenerating(false);
    }
  }, [lessonId, currentVersion, stages, updateSlidesHTML, markDirty, queryClient, createBlobUrl]);

  // ── Generate Teaching Notes ──────────────────────────────────────────────

  const handleGenerateNotes = useCallback(async () => {
    if (!currentSlide) return;
    setIsGeneratingNotes(true);
    try {
      let generatedNotes = '';
      await new Promise<void>((resolve) => {
        void aiSSEService.generateTeachingNotes(
          {
            lesson_id: lessonId,
            version: currentVersion?.version_number ?? 1,
            stage_name: currentSlide.stageName,
            slide_number: currentSlide.slide_number,
          },
          {
            onEvent: (event) => {
              if (event.type === 'teaching_notes_complete') {
                generatedNotes = event.data.speaker_notes;
              }
            },
            onComplete: resolve,
          }
        );
      });
      patchSlideNotes(currentSlide.stageName, currentSlide.slide_number, generatedNotes);
    } finally {
      setIsGeneratingNotes(false);
    }
  }, [currentSlide, lessonId, currentVersion, patchSlideNotes]);

  // ── Proceed to Phase 3 ───────────────────────────────────────────────────

  const handleProceed = useCallback(async () => {
    setProceedError('');
    if (allSlides.length === 0) {
      setProceedError('No slides found. Please regenerate before proceeding.');
      return;
    }
    await flushEdits();

    // Seed image records from placeholders in the slide HTML, trigger generation,
    // then prefetch into the React Query cache so Phase 3 renders immediately.
    const placeholders = extractPlaceholders(fullMarpHTMLRef.current);
    if (placeholders.length > 0) {
      await imageService.seedImages(lessonId, versionNumber, placeholders);
      await imageService.generateAllImages(lessonId, versionNumber);
    }
    await queryClient.fetchQuery({
      queryKey: ['images', lessonId, versionNumber],
      queryFn: () => imageService.getImages(lessonId, versionNumber),
    });

    await lessonService.saveVersion(lessonId, currentVersion?.version_number ?? 1, {
      slides_html: slidesHTMLRef.current,
      phase: 3,
    });
    setPhase(3);
  }, [allSlides, flushEdits, lessonId, versionNumber, currentVersion, setPhase, queryClient]);

  // ── Empty / generating state ─────────────────────────────────────────────

  if (allSlides.length === 0 || !fullMarpHTML) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', gap: 2, color: 'text.disabled',
      }}>
        {isRegenerating ? (
          <>
            <CircularProgress size={48} />
            <Typography color="text.secondary">Generating slides…</Typography>
          </>
        ) : (
          <>
            <AutoAwesomeIcon sx={{ fontSize: 48 }} />
            <Typography>No slides generated yet.</Typography>
            {!readOnly && (
              <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                onClick={() => setRegenerateConfirmOpen(true)}>
                Generate Slides
              </Button>
            )}
          </>
        )}

        {/* Regenerate confirm (also needed in empty state) */}
        <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)}>
          <DialogTitle>Generate Slides?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Slides will be generated from the current lesson plan using the MARP pipeline.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegenerateConfirmOpen(false)}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={() => void handleRegenerateAll()}
              disabled={isRegenerating}>
              Generate
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── Main editor ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Full MARP deck — Bespoke.js owns navigation; arrow keys / clicks work inside */}
      <Box sx={{
        flex: 1, overflow: 'hidden', bgcolor: '#f0f0f0',
        display: 'flex', flexDirection: 'row', gap: 0,
      }}>
        <Box sx={{
          flex: 1, overflow: 'hidden',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 2,
        }}>
          <Paper elevation={3} sx={{
            width: '100%', maxWidth: 900,
            aspectRatio: '16/9', overflow: 'hidden', borderRadius: 2,
          }}>
            <iframe
              ref={iframeRef}
              src={blobUrl || undefined}
              title="Lesson Slides"
              style={{ width: '100%', height: '100%', border: 'none' }}
              onLoad={handleIframeLoad}
            />
          </Paper>
        </Box>

        {/* Teaching notes side panel */}
        {notesOpen && (
          <Box sx={{
            width: 300, flexShrink: 0, bgcolor: 'background.paper',
            borderLeft: '1px solid', borderColor: 'divider',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Panel header */}
            <Box sx={{
              px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: 'rgba(25,118,210,0.06)',
            }}>
              <SpeakerNotesIcon sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} color="primary.main" display="block" noWrap>
                  Teaching Notes
                </Typography>
                {currentSlide && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ fontSize: '0.65rem' }}>
                    {currentSlide.stageName} · Slide {currentMarpIndex + 1}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Notes content */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              {currentSlide?.speaker_notes ? (
                <Box sx={{ p: 1.5, flex: 1 }}>
                  {currentSlide.speaker_notes
                    .split('\n')
                    .filter((line) => line.trim() !== '')
                    .map((line, i) => {
                      const isBullet = /^[-•*]\s/.test(line.trim());
                      const isLabel = /^[A-Z][^:]{1,20}:\s/.test(line.trim());
                      const clean = isBullet ? line.trim().replace(/^[-•*]\s/, '') : line.trim();
                      return (
                        <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'flex-start' }}>
                          {isBullet && (
                            <Box component="span" sx={{
                              width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main',
                              flexShrink: 0, mt: '6px',
                            }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              lineHeight: 1.6,
                              color: isLabel ? 'text.secondary' : 'text.primary',
                              fontWeight: isLabel ? 600 : 400,
                              flex: 1,
                            }}
                          >
                            {clean}
                          </Typography>
                        </Box>
                      );
                    })}
                </Box>
              ) : (
                <Box sx={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', p: 3, gap: 1,
                }}>
                  <SpeakerNotesIcon sx={{ fontSize: 32, color: 'text.disabled', opacity: 0.4 }} />
                  <Typography variant="caption" color="text.disabled" textAlign="center">
                    No teaching notes for this slide.
                  </Typography>
                </Box>
              )}

              {/* Generate button */}
              {!readOnly && (
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    fullWidth
                    size="small"
                    variant={currentSlide?.speaker_notes ? 'outlined' : 'contained'}
                    startIcon={isGeneratingNotes ? <CircularProgress size={13} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                    disabled={isGeneratingNotes || !currentSlide}
                    onClick={() => void handleGenerateNotes()}
                    sx={{ borderRadius: 1.5, fontSize: '0.75rem' }}
                  >
                    {isGeneratingNotes
                      ? 'Generating…'
                      : currentSlide?.speaker_notes
                      ? 'Regenerate for slide'
                      : 'Generate for slide'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* AI chat panel */}
      <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: chatExpanded ? '1px solid' : 'none', borderColor: 'divider' }}>
          {/* Toggle row */}
          <Box onClick={() => setChatExpanded((v) => !v)} sx={{
            flex: 1, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            bgcolor: chatExpanded ? 'action.hover' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
            userSelect: 'none',
          }}>
            <AutoAwesomeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ flex: 1 }}>
              AI Refinement
            </Typography>
            {chatExpanded
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
          </Box>

          {/* Save status + notes toggle + regenerate */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
            {saveStatus === 'saving' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={10} />
                <Typography variant="caption" color="text.secondary">Saving…</Typography>
              </Box>
            )}
            {saveStatus === 'saved' && (
              <Typography variant="caption" color="success.main">Saved</Typography>
            )}
            <Tooltip title={notesOpen ? 'Hide teaching notes' : 'Show teaching notes'}>
              <IconButton size="small" onClick={() => setNotesOpen((v) => !v)}
                color={notesOpen ? 'primary' : 'default'}>
                {notesOpen
                  ? <SpeakerNotesOffIcon fontSize="small" />
                  : <SpeakerNotesIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {!readOnly && (
              <Tooltip title="Regenerate all slides">
                <span>
                  <IconButton size="small" onClick={() => setRegenerateConfirmOpen(true)}
                    disabled={isRegenerating}>
                    {isRegenerating
                      ? <CircularProgress size={16} />
                      : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Collapse in={chatExpanded}>
          <Box sx={{ px: 2, pb: 1.5, height: 240 }}>
            <ChatInterface
              key={currentMarpIndex}
              messages={currentSlide?.chat_history ?? []}
              onSend={handleChatSend}
              isLoading={isChatLoading}
              placeholder='Ask AI to refine the current slide… e.g. "add an image placeholder on the right"'
            />
          </Box>
        </Collapse>
      </Box>

      {/* Proceed footer */}
      <Box sx={{
        flexShrink: 0, px: 2, py: 1.5,
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2,
        borderTop: '1px solid', borderColor: 'divider',
      }}>
        {proceedError && <Alert severity="error">{proceedError}</Alert>}
        <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
          onClick={() => void handleProceed()}
          disabled={readOnly || allSlides.length === 0}
          sx={{ borderRadius: 2, px: 4 }}>
          Proceed to Phase 3
        </Button>
      </Box>

      {/* Regenerate confirm */}
      <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)}>
        <DialogTitle>Regenerate All Slides?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will regenerate all slides via the MARP pipeline, replacing all current content
            and chat history. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateConfirmOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => void handleRegenerateAll()}
            disabled={isRegenerating}
            startIcon={isRegenerating ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Regenerate All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
